import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@components/moleculs/autocomplete';
import { TextField } from '@components/moleculs/text-field';
import type { LogoSelectorProps } from '@components/organisms/language-selector/types';
import type { LangEnum } from '@models/enums';
import type { AutocompleteOption } from '@models/types';
import { AVAILABLE_LANGUAGES } from '@modules/docs-verification-widget/localization';
import { styled } from '@mui/material/styles';

const LanguageSelectorComponent: FC<LogoSelectorProps> = ({ className, onChange }) => {
    const { t, i18n } = useTranslation();

    const options = useMemo(
        () =>
            AVAILABLE_LANGUAGES.map((language) => ({
                label: t(`languageSelector.language.${language}`),
                value: language,
            })),
        [t],
    );

    const value = useMemo(
        () => options.find((option) => option.value === i18n.language),
        [i18n.language, options],
    );

    return (
        <Autocomplete
            value={value}
            fullWidth={true}
            onChange={(e, option) => {
                if (option) {
                    const { value: val } = option as AutocompleteOption<LangEnum>;

                    i18n.changeLanguage((option as AutocompleteOption).value);
                    onChange?.(val);
                }
            }}
            isOptionEqualToValue={(option, val) => option.label === val.label}
            renderInput={({ slotProps, ...rest }) => (
                <TextField
                    {...rest}
                    InputProps={{
                        ...slotProps.input,
                        readOnly: true,
                    }}
                    inputProps={{
                        ...slotProps.htmlInput,
                        readOnly: true,
                    }}
                    inputSize='s'
                />
            )}
            options={options}
            disableClearable={true}
            className={className}
        />
    );
};

export const LanguageSelector = styled(LanguageSelectorComponent)({});
